import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, Settings } from 'lucide-react';
import { useGetCustomTokenPairs, useRemoveCustomTokenPair } from '../hooks/useQueries';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface CustomTokensManagerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function CustomTokensManager({ open, onOpenChange }: CustomTokensManagerProps) {
    const { data, isLoading } = useGetCustomTokenPairs();
    // Ensure customTokenPairs is always defined - critical for preventing runtime errors
    const customTokenPairs: any[] = Array.isArray(data) ? data : [];
    const { mutate: removeCustomTokenPair, isPending: isRemoving } = useRemoveCustomTokenPair();

    const handleRemove = (symbol: string) => {
        if (confirm(`Are you sure you want to remove ${symbol}?`)) {
            removeCustomTokenPair(symbol, {
                onSuccess: () => {
                    toast.success(`${symbol} removed successfully`);
                },
                onError: (error: any) => {
                    toast.error(`Failed to remove ${symbol}: ${error?.message || 'Unknown error'}`);
                },
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[80vh]">
                <DialogHeader>
                    <DialogTitle>Manage Custom Tokens</DialogTitle>
                    <DialogDescription>
                        View and manage your custom token pairs. Custom tokens are user-added and may not have automatic balance or price data.
                    </DialogDescription>
                </DialogHeader>

                <ScrollArea className="max-h-[60vh] pr-4">
                    {isLoading ? (
                        <div className="text-center py-8 text-muted-foreground">
                            Loading custom tokens...
                        </div>
                    ) : !customTokenPairs || customTokenPairs.length === 0 ? (
                        <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                No custom tokens found. Add custom tokens using the "+" button in the swap interface.
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <div className="space-y-3">
                            {customTokenPairs.map((token) => (
                                <div
                                    key={token.symbol}
                                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                                >
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-semibold">{token.symbol}</span>
                                            <Badge variant="outline" className="text-xs">Custom</Badge>
                                        </div>
                                        <div className="text-sm text-muted-foreground">{token.name}</div>
                                        {token.network && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                                Network: {token.network}
                                            </div>
                                        )}
                                        {token.contractAddress && (
                                            <div className="text-xs text-muted-foreground mt-1 font-mono">
                                                {token.contractAddress.slice(0, 10)}...{token.contractAddress.slice(-8)}
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleRemove(token.symbol)}
                                        disabled={isRemoving}
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
}

